import type { FC, PropsWithChildren } from "react";
import { useMemo } from "react";
import type { TagContainerProps } from "@ptsd/uikit";
import { Button, Tag, Card as UIKitCard } from "@ptsd/uikit";
import type { TextProps, ViewProps } from "tamagui";
import { Anchor, Text, useMedia, View } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { amountToHuman } from "~/utils/formatters";
import config from "~/config";
import { IconClock } from "./icons/icon-clock";

export const Card: FC<ViewProps> = ({ $gtSm, ...props }) => {
  return (
    <UIKitCard
      onPress={(e) => e.stopPropagation()}
      margin={"$2"}
      paddingVertical={"48px"}
      width={"auto"}
      $gtSm={{ width: "100%", ...$gtSm }}
      maxWidth={"600px"}
      flex={1}
      {...props}
    />
  );
};

export const Label: FC<TagContainerProps> = (props) => {
  return (
    <View flexDirection={"row"} paddingBottom={"$2.5"} minHeight={"$7.5"}>
      {!!props.children && (
        <Tag
          variant={"primary"}
          paddingVertical={"$1.2"}
          rounded="xl"
          size={"lg"}
          {...props}
        />
      )}
    </View>
  );
};

export const Title: FC<TextProps> = (props) => {
  return (
    <Text
      fontWeight={"bold"}
      fontFamily={"$heading"}
      paddingBottom={"$1"}
      fontSize={24}
      $gtSm={{ fontSize: 36 }}
      {...props}
    />
  );
};

export const Description: FC<TextProps> = (props) => {
  return (
    <Text
      fontSize={16}
      color={"#45556C"}
      fontFamily={"$body"}
      paddingBottom={"$1"}
      $gtSm={{ fontSize: 22 }}
      {...props}
    />
  );
};

export const Price: FC<{ plan: PayPlan }> = ({ plan }) => {
  const validUntil = useMemo(
    () =>
      new Date(Date.now() + 1000 * 60 * 60 * 24 * 3).toLocaleDateString(
        "uk-UA",
        { day: "numeric", month: "numeric", year: "numeric" },
      ),
    [],
  );

  return (
    <View
      marginTop="$2"
      marginBottom="$2"
      padding={"24px"}
      alignItems={"center"}
      borderRadius={"$4"}
      borderColor={"$gray-200"}
      borderWidth={2}
    >
      <Text fontSize={24} fontFamily={"$body"} $gtSm={{ fontSize: 36 }}>
        {plan.amountBeforeSale !== null && (
          <Text
            color={"$gray-200"}
            fontWeight={"400"}
            textDecorationLine={"line-through"}
            marginRight={12}
          >
            {amountToHuman(plan.amountBeforeSale)}
          </Text>
        )}
        <Text
          fontWeight={"600"}
          style={{
            background:
              "linear-gradient(90deg,rgba(246, 51, 154, 1) 0%, rgba(255, 32, 86, 1) 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            WebkitTextFillColor: "transparent",
          }}
        >
          {amountToHuman(plan.amount)}
        </Text>
      </Text>
      {plan.amountBeforeSale !== null && (
        <View
          alignItems={"center"}
          paddingTop={"$1.5"}
          $gtSm={{ paddingTop: "$1" }}
          flexDirection={"row"}
          gap={"$0.5"}
          justifyContent={"center"}
        >
          <IconClock />
          <Text
            alignItems={"center"}
            color={"$gray-secondary-400"}
            fontSize={14}
            fontFamily={"$body"}
          >
            Пропозиція діє до {validUntil}
          </Text>
        </View>
      )}
    </View>
  );
};

export const Action: FC<PropsWithChildren<{ plan: PayPlan }>> = ({
  plan,
  children,
}) => {
  const { sm } = useMedia();
  return (
    <Anchor
      href={new URL("payment/link/" + plan.id, config.API_URL).toString()}
      textDecorationLine="none"
      asChild
    >
      <Button variant={"outlined"} size={sm ? "md" : "lg"} rounded={"lg"}>
        {children}
      </Button>
    </Anchor>
  );
};

export const Benefits: FC<ViewProps> = (props) => {
  return (
    <View
      flex={1}
      flexDirection={"column"}
      gap={"$2"}
      marginBottom={"$2.5"}
      {...props}
    />
  );
};

const BenefitsItem: FC<PropsWithChildren<{ Icon: FC }>> = ({
  Icon,
  children,
}) => {
  return (
    <View
      gap={"$2"}
      flexDirection={"row"}
      marginHorizontal={"$1.5"}
      alignItems={"center"}
    >
      <Icon />
      <Text fontSize={16} fontFamily={"$body"} marginLeft={"$0.5"}>
        {children}
      </Text>
    </View>
  );
};

export const PayPlan = {
  Card,
  Label,
  Title,
  Description,
  Price,
  Benefits,
  BenefitsItem,
  Action,
};

export type PayPlan = RouterOutputs["payments"]["getPlans"]["plans"][number];
